// =============================================================================
// Partner venue profile editor — opened from the venue profile's "Edit
// profile" button. A partner can change exactly what firestore.rules
// (isVenueOwnerProfileEdit) lets them: cover, profile picture, bio and
// category. Name, owner and follower count stay admin/server-managed. Images
// upload to venues/{venueId}/ (owner/admin only in storage.rules).
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import TonyteBackground from '../components/TonyteBackground';
import TonyteCropModal, { type ImagePreviewSession } from '../components/TonyteCropModal';
import { auth, firestore, uploadVenueImage } from '../firebase';
import { useProfile } from '../hooks/useProfile';
import { VENUE_CATEGORIES } from '../hooks/useVenueVibes';
import { useI18n } from '../i18n';
import { Radius, Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/types';
import type { RootStackParamList } from '../types';
import { pickImageFromLibrary } from '../utils/permissions';

type Props = NativeStackScreenProps<RootStackParamList, 'EditVenueProfile'>;

const BIO_LIMIT = 600; // mirrored in firestore.rules

type VenueDoc = { name?: string; bio?: string; category?: string; coverUrl?: string; logoUrl?: string; ownerUid?: string };

export default function EditVenueProfileScreen({ route, navigation }: Props) {
  const { venueId } = route.params;
  const { t } = useI18n();
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const userId = auth.currentUser?.uid ?? null;
  const { profile } = useProfile(userId);
  const isAdmin = profile?.role === 'admin' || profile?.role === 'co-admin';

  const [venue, setVenue] = useState<VenueDoc | null>(null);
  const [bio, setBio] = useState('');
  const [category, setCategory] = useState('');
  const [newCover, setNewCover] = useState<string | null>(null);
  const [newLogo, setNewLogo] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Picked photo being framed: move, pinch/slider zoom, then crop to the exact
  // shape it's shown in (2.5:1 cover, round profile picture).
  const [cropSession, setCropSession] = useState<ImagePreviewSession | null>(null);
  // Full-resolution photos the partner picked, so "Adjust" re-frames from the
  // original instead of re-cropping an already-cropped (smaller) image.
  const [originals, setOriginals] = useState<{ cover?: string; logo?: string }>({});

  useEffect(() => {
    getDoc(doc(firestore, 'venues', venueId))
      .then(snapshot => {
        const data = (snapshot.data() ?? {}) as VenueDoc;
        setVenue(data);
        setBio(data.bio ?? '');
        setCategory(data.category ?? '');
      })
      .catch(() => Alert.alert(t('venueProfiles.editLoadFailed'), t('common.tryAgain')));
  }, [t, venueId]);

  const canEdit = Boolean(venue) && (isAdmin || (Boolean(userId) && venue?.ownerUid === userId));
  const coverSource = newCover || venue?.coverUrl;
  const logoSource = newLogo || venue?.logoUrl;
  const changed = Boolean(newCover || newLogo) || bio !== (venue?.bio ?? '') || category !== (venue?.category ?? '');

  const pick = async (kind: 'cover' | 'logo') => {
    const result = await pickImageFromLibrary({ mediaType: 'photo', quality: 1 });
    const uri = result?.assets?.[0]?.uri;
    if (!uri) return;
    setOriginals(current => ({ ...current, [kind]: uri }));
    openCrop(kind, uri);
  };

  const openCrop = (kind: 'cover' | 'logo', uri: string) => {
    setCropSession(kind === 'cover'
      ? { kind: 'cover', uri, aspectRatio: '5:2' }
      : { kind: 'profile', uri, aspectRatio: '1:1' });
  };

  // Re-frame the current image: the original pick if there is one, otherwise
  // the saved image.
  const adjust = (kind: 'cover' | 'logo') => {
    const uri = originals[kind] || (kind === 'cover' ? venue?.coverUrl : venue?.logoUrl);
    if (uri) openCrop(kind, uri);
  };

  const applyCrop = (croppedUri: string) => {
    if (cropSession?.kind === 'cover') setNewCover(croppedUri);
    else setNewLogo(croppedUri);
    setCropSession(null);
  };

  const save = async () => {
    if (!venue || !changed || saving) return;
    setSaving(true);
    try {
      const update: Record<string, unknown> = { bio: bio.trim(), updatedAt: serverTimestamp() };
      if (category) update.category = category;
      if (newCover) update.coverUrl = (await uploadVenueImage(venueId, newCover, 'cover')).downloadUrl;
      if (newLogo) update.logoUrl = (await uploadVenueImage(venueId, newLogo, 'logo')).downloadUrl;
      await updateDoc(doc(firestore, 'venues', venueId), update);
      navigation.goBack();
    } catch (error) {
      console.warn('[Tonyte][EditVenueProfile] save failed', error);
      Alert.alert(t('venueProfiles.editSaveFailed'), t('common.tryAgain'));
    } finally {
      setSaving(false);
    }
  };

  const iconColor = theme.isDark ? '#fff' : theme.colors.icon;

  return (
    <TonyteBackground appearanceAware>
      <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
          <TouchableOpacity style={[styles.iconButton, appearance.control]} onPress={() => navigation.goBack()}>
            <Icon name="x" size={20} color={iconColor} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, appearance.textPrimary]} numberOfLines={1}>{t('venueProfiles.editProfile')}</Text>
          <TouchableOpacity
            style={[styles.saveButton, (!changed || saving || !canEdit) && styles.disabled]}
            disabled={!changed || saving || !canEdit}
            onPress={() => void save()}
          >
            {saving ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.saveText}>{t('common.save')}</Text>}
          </TouchableOpacity>
        </View>

        {!venue ? (
          <ActivityIndicator style={styles.loader} color={theme.isDark ? '#fff' : theme.colors.accent} />
        ) : !canEdit ? (
          <Text style={[styles.message, appearance.textSecondary]}>{t('venueProfiles.editNotAllowed')}</Text>
        ) : (
          <ScrollView contentContainerStyle={[styles.scroll, { paddingBottom: insets.bottom + 40 }]} keyboardShouldPersistTaps="handled">
            <Text style={[styles.venueName, appearance.textPrimary]}>{venue.name}</Text>

            <Text style={[styles.label, appearance.textSecondary]}>{t('venueProfiles.coverPhoto')}</Text>
            <TouchableOpacity activeOpacity={0.85} style={[styles.cover, appearance.surface]} onPress={() => void pick('cover')}>
              {coverSource
                ? <Image source={{ uri: coverSource }} style={StyleSheet.absoluteFill} resizeMode="cover" />
                : <LinearGradient colors={['#2F145F', '#111827', '#060711']} style={StyleSheet.absoluteFill} />}
              <View style={styles.badgeRow}>
                {coverSource ? (
                  <TouchableOpacity style={styles.changeBadge} onPress={() => adjust('cover')}>
                    <Icon name="move" size={14} color="#fff" />
                    <Text style={styles.changeText}>{t('venueProfiles.adjustPhoto')}</Text>
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity style={styles.changeBadge} onPress={() => void pick('cover')}>
                  <Icon name="camera" size={14} color="#fff" />
                  <Text style={styles.changeText}>{t('venueProfiles.changePhoto')}</Text>
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
            <Text style={[styles.hint, appearance.textMuted]}>{t('venueProfiles.coverHint')}</Text>

            <Text style={[styles.label, appearance.textSecondary]}>{t('venueProfiles.profilePhoto')}</Text>
            <TouchableOpacity activeOpacity={0.85} style={styles.logoRow} onPress={() => void pick('logo')}>
              <View style={[styles.logo, appearance.surface]}>
                {logoSource
                  ? <Image source={{ uri: logoSource }} style={styles.logoImage} />
                  : <Text style={[styles.logoLetter, appearance.textPrimary]}>{(venue.name || '?').slice(0, 1).toUpperCase()}</Text>}
              </View>
              <View style={[styles.logoChange, appearance.control]}>
                <Icon name="camera" size={14} color={iconColor} />
                <Text style={[styles.logoChangeText, appearance.textPrimary]}>{t('venueProfiles.changePhoto')}</Text>
              </View>
              {logoSource ? (
                <TouchableOpacity style={[styles.logoChange, appearance.control]} onPress={() => adjust('logo')}>
                  <Icon name="move" size={14} color={iconColor} />
                  <Text style={[styles.logoChangeText, appearance.textPrimary]}>{t('venueProfiles.adjustPhoto')}</Text>
                </TouchableOpacity>
              ) : null}
            </TouchableOpacity>

            <Text style={[styles.label, appearance.textSecondary]}>{t('venueProfiles.bioLabel')}</Text>
            <TextInput
              style={[styles.bioInput, appearance.input]}
              value={bio}
              onChangeText={setBio}
              maxLength={BIO_LIMIT}
              multiline
              placeholder={t('venueProfiles.bioPlaceholder')}
              placeholderTextColor={theme.isDark ? 'rgba(255,255,255,0.4)' : theme.colors.textPlaceholder}
            />
            <Text style={[styles.counter, appearance.textMuted]}>{bio.length}/{BIO_LIMIT}</Text>

            <Text style={[styles.label, appearance.textSecondary]}>{t('venueProfiles.categoryLabel')}</Text>
            <View style={styles.chips}>
              {VENUE_CATEGORIES.map(item => (
                <TouchableOpacity
                  key={item}
                  onPress={() => setCategory(item)}
                  style={[styles.chip, appearance.chip, category === item && styles.chipActive, category === item && appearance.chipActive]}
                >
                  <Text style={[styles.chipText, appearance.textSecondary, category === item && styles.chipTextActive, category === item && appearance.textPrimary]}>{item}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        )}
      </KeyboardAvoidingView>
      {cropSession ? (
        <TonyteCropModal session={cropSession} onCrop={applyCrop} onCancel={() => setCropSession(null)} />
      ) : null}
    </TonyteBackground>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingBottom: 12 },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  headerTitle: { flex: 1, color: '#fff', fontSize: 18, fontWeight: Typography.extrabold },
  saveButton: { minWidth: 72, height: 38, borderRadius: 19, paddingHorizontal: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F27D26' },
  saveText: { color: '#fff', fontSize: 14, fontWeight: Typography.extrabold },
  disabled: { opacity: 0.45 },
  loader: { marginTop: 60 },
  message: { color: 'rgba(255,255,255,0.62)', fontSize: 14, textAlign: 'center', padding: 24 },
  scroll: { paddingHorizontal: 16 },
  venueName: { color: '#fff', fontSize: 22, fontWeight: Typography.extrabold, marginBottom: 6 },
  label: { color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: Typography.bold, marginTop: 18, marginBottom: 8 },
  // Same 2.5:1 shape as the crop frame, the Vibe at Venues card and the
  // venue profile header, so what the partner frames is exactly what shows.
  cover: { width: '100%', aspectRatio: 2.5, borderRadius: 16, overflow: 'hidden', backgroundColor: '#111827' },
  badgeRow: { position: 'absolute', right: 10, bottom: 10, flexDirection: 'row', gap: 8 },
  changeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  changeText: { color: '#fff', fontSize: 12, fontWeight: Typography.bold },
  hint: { color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 6 },
  logoRow: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  logo: {
    width: 84,
    height: 84,
    borderRadius: 42,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1b2331',
  },
  logoImage: { width: '100%', height: '100%' },
  logoLetter: { color: '#fff', fontSize: 30, fontWeight: Typography.extrabold },
  logoChange: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  logoChangeText: { color: '#fff', fontSize: 13, fontWeight: Typography.bold },
  bioInput: {
    minHeight: 120,
    borderRadius: 14,
    padding: 12,
    color: '#fff',
    fontSize: 14,
    lineHeight: 20,
    textAlignVertical: 'top',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  counter: { color: 'rgba(255,255,255,0.45)', fontSize: 11, textAlign: 'right', marginTop: 4 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: {
    height: 34,
    paddingHorizontal: 14,
    borderRadius: Radius.pill,
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  chipActive: { backgroundColor: 'rgba(242,125,38,0.2)', borderColor: 'rgba(255,154,60,0.55)' },
  chipText: { color: 'rgba(255,255,255,0.68)', fontSize: 12, fontWeight: Typography.bold },
  chipTextActive: { color: '#fff' },
});

// Midnight stays the canonical StyleSheet above; Daylight only overrides.
const createThemeStyles = (theme: TonyteTheme) => {
  const c = theme.colors;
  return StyleSheet.create({
    control: daylightStyle(theme, { backgroundColor: c.controlBackground, borderColor: c.border }),
    surface: daylightStyle(theme, { backgroundColor: c.backgroundSecondary }),
    input: daylightStyle(theme, { backgroundColor: c.inputBackground, borderColor: c.border, color: c.textPrimary }),
    chip: daylightStyle(theme, { backgroundColor: c.chipBackground, borderColor: c.border }),
    chipActive: daylightStyle(theme, { backgroundColor: c.chipBackgroundActive, borderColor: 'rgba(242,125,38,0.36)' }),
    textPrimary: daylightStyle(theme, { color: c.textPrimary }),
    textSecondary: daylightStyle(theme, { color: c.textSecondary }),
    textMuted: daylightStyle(theme, { color: c.textMuted }),
  });
};
